import { createFeature, createReducer, on } from '@ngrx/store';

import { ScannedCode } from 'app/features/scan-flow/models';
import { isActivitySupportedForScanFlow } from 'app/features/scan-flow/utils';
import { isRequiredActivityEntityEqual } from 'app/features/scan-flow/utils/scan-activity.utils';
import { ProductBoxRegistration } from 'app/shared/components/product-box/models';
import { RowSelectionItem } from 'app/shared/features/map-row-selector/models';
import { sortRowSelection } from 'app/shared/features/map-row-selector/utils';
import { done, getInitialProgressState, ProgressState, running } from 'app/shared/models';
import { sortProductBoxRegistrations } from 'app/shared/utils';
import { resetApiConfig } from 'app/state/api-config/api-config.actions';

import {
    getActivityCode,
    getLocationNumbersFromScannedCodes,
    sortScannedCodes,
} from '../../utils/scanned-code-list.utils';
import { removeScannedCodeFromFlow, setScanFlowScannedCodes } from '../scanned-codes/scanned-codes.actions';
import {
    cancelScheduledScanFlowRegistration,
    scheduleScanFlowRegistration,
} from './scan-flow-labor-registration/scan-flow-labor-registration.actions';
import {
    applyScanFlowRegistrationDataFromStorage,
    changeScanFlowRowSelection,
    initScanFlow,
    initScanFlowSuccess,
    resetPartialScanFlowRegistrationData,
    resetScanFlowRegistrationData,
    setActivityInScanFlow,
    setBatchInScanFlow,
    setScanFlowAmountResult,
    setScanFlowEmployeeNumber,
    setScanFlowProductBoxRegistrations,
    userInputForScanFlowEnded,
    userInputForScanFlowStarted,
} from './scan-flow.actions';
import {
    setScanFlowStateWithNewActivityNumber,
    setScanFlowStateWithSimilarActivityNumber,
} from './scan-flow.utils';

export const scanFlowFeatureKey = 'scanFlow';

export interface ScanFlowRegistrationData {
    amountResult: number | undefined;
    batchNumber: number | undefined;
    employeeNumber: number | undefined;
    productBoxRegistrations: ProductBoxRegistration[];
    rowSelection: RowSelectionItem[];
    scannedCodes: ScannedCode[];
}

export interface ScanFlowState {
    data: ScanFlowRegistrationData;
    initializationProgress: ProgressState;
    scheduledRegistrationDate: number | undefined;
    waitingForUserInput: boolean;
}

export const emptyScanFlowRegistrationData: ScanFlowRegistrationData = {
    amountResult: undefined,
    batchNumber: undefined,
    employeeNumber: undefined,
    productBoxRegistrations: [],
    rowSelection: [],
    /**
     * scannedCodes: The authoritative list of scanned codes for the current scan flow session.
     * This is the single source of truth for scanned codes in the scan flow feature.
     * It is also kept in local storage for persistence across sessions.
     */
    scannedCodes: [],
};

export const initialState: ScanFlowState = {
    data: emptyScanFlowRegistrationData,
    initializationProgress: getInitialProgressState(),
    scheduledRegistrationDate: undefined,
    waitingForUserInput: false,
};

export const reducer = createReducer(
    initialState,
    on(initScanFlow, (state) => {
        if (state.initializationProgress.done) return state;
        return { ...state, initializationProgress: running() };
    }),
    on(initScanFlowSuccess, (state) => ({ ...state, initializationProgress: done() })),
    on(resetScanFlowRegistrationData, resetApiConfig, (state) => ({
        ...state,
        data: emptyScanFlowRegistrationData,
    })),
    on(resetPartialScanFlowRegistrationData, (state) => ({
        ...state,
        data: {
            ...emptyScanFlowRegistrationData,
            employeeNumber: state.data.employeeNumber,
        },
    })),
    on(applyScanFlowRegistrationDataFromStorage, (state, { registrationDataFromStorage }) => ({
        ...state,
        initializationProgress: done(),
        data: registrationDataFromStorage ?? emptyScanFlowRegistrationData,
    })),
    on(setScanFlowScannedCodes, (state, { scannedCode }) => {
        const oldCodes = state.data.scannedCodes;
        const oldCodesWithoutBatch = oldCodes.filter((code) => code.codeType !== 'batch');
        const isScannedCodeNumberExist = oldCodes.some((oldCode) => oldCode.code === scannedCode.code);

        let newCodes: ScannedCode[] = [];
        let rowSelection = state.data.rowSelection;
        let batchNumber: number | undefined;

        switch (scannedCode.codeType) {
            case 'batch':
                if (isScannedCodeNumberExist) return state;
                // If new batch code is scanned, replace the existing batch code.
                newCodes = sortScannedCodes([...oldCodesWithoutBatch, scannedCode]);
                batchNumber = scannedCode.number;
                break;
            default:
                if (isScannedCodeNumberExist) return state;
                if (scannedCode.codeType === 'location' && scannedCode.number)
                    rowSelection = sortRowSelection([
                        ...rowSelection,
                        { rowNumber: scannedCode.number, registrationStatus: 'done' },
                    ]);

                newCodes = sortScannedCodes([...oldCodes, scannedCode]);
                break;
        }

        return {
            ...state,
            data: {
                ...state.data,
                batchNumber,
                scannedCodes: newCodes.filter((newCode) => newCode !== null),
                rowSelection,
            },
        };
    }),
    on(setScanFlowEmployeeNumber, (state, { employeeNumber }) =>
        state.data.employeeNumber === employeeNumber
            ? state
            : {
                  ...state,
                  data: { ...state.data, employeeNumber },
              },
    ),
    on(setScanFlowAmountResult, (state, { amountResult }) =>
        state.data.amountResult === amountResult
            ? state
            : {
                  ...state,
                  data: { ...state.data, amountResult },
              },
    ),
    on(setScanFlowProductBoxRegistrations, (state, { updatedProductBoxRegistrations }) => {
        const sortedCurrentRegistrations = sortProductBoxRegistrations(state.data.productBoxRegistrations);
        const sortedUpdatedRegistrationList = sortProductBoxRegistrations(updatedProductBoxRegistrations);

        const currentListJson = JSON.stringify(sortedCurrentRegistrations);
        const updatedListJson = JSON.stringify(sortedUpdatedRegistrationList);

        const registrationsRemainTheSame = currentListJson === updatedListJson;
        if (registrationsRemainTheSame) {
            return state;
        }

        return {
            ...state,
            data: {
                ...state.data,
                productBoxRegistrations: sortedUpdatedRegistrationList,
            },
        };
    }),
    on(setActivityInScanFlow, (state, action) => {
        const newActivityCode = action.activityCode;
        const { keepRegistrationData, isContainerMeasurement, batchCode } = action;

        // If no new new activity is provided, reset the scan flow data
        if (!newActivityCode) {
            return {
                ...state,
                data: {
                    ...state.data,
                    amountResult: undefined,
                    productBoxRegistrations: [],
                    rowSelection: [],
                    scannedCodes: [],
                },
            };
        }

        const newActivity = newActivityCode.entity;
        if (!isActivitySupportedForScanFlow(newActivity)) {
            return state;
        }
        const currentActivityCode = getActivityCode(state.data.scannedCodes);

        let partialRegistrationData: Partial<ScanFlowRegistrationData> = {};
        /**
         * This condition only applies when:
         * Keep registration data
         * - Click same activity in activity history
         * - Scan same activity number again
         * - Monitoring activity in master data (Update master data)
         * Registration has been sent
         */
        if (currentActivityCode?.number === newActivityCode.number) {
            if (
                keepRegistrationData &&
                isRequiredActivityEntityEqual(newActivityCode.entity, currentActivityCode?.entity)
            ) {
                return state;
            }
            partialRegistrationData = setScanFlowStateWithSimilarActivityNumber(
                state.data,
                newActivityCode,
                isContainerMeasurement,
            );
        } else {
            /**
             * Reset scan flow registration data once new activity scanned
             * This condition only applies when:
             * Click new activity in activity history
             * Activity number has changed (monitoring activity in master data)
             * Local storage expired, keepRegistrationData is true
             */
            partialRegistrationData = setScanFlowStateWithNewActivityNumber(
                state.data,
                newActivityCode,
                batchCode,
                keepRegistrationData,
            );
        }

        return {
            ...state,
            data: {
                ...state.data,
                ...partialRegistrationData,
            },
        };
    }),
    on(setBatchInScanFlow, (state, { batchCode }) => {
        // If there's an existing batch code, we remove it from the scanned codes and add the new one.
        const scannedCodes = state.data.scannedCodes.filter((code) => code.codeType !== 'batch');
        return {
            ...state,
            data: {
                ...state.data,
                batchNumber: batchCode.number,
                scannedCodes: [...scannedCodes, batchCode],
            },
        };
    }),
    on(changeScanFlowRowSelection, (state, { rowNumber, registrationStatus }) => {
        const rowNumbers = getLocationNumbersFromScannedCodes(state.data.scannedCodes);
        if (!rowNumbers.includes(rowNumber)) return state;

        const oldRowSelection = state.data.rowSelection;

        if (
            oldRowSelection.find(
                (currentRowSelectionItem) =>
                    currentRowSelectionItem.rowNumber === rowNumber &&
                    currentRowSelectionItem.registrationStatus === registrationStatus,
            )
        ) {
            return state;
        }

        const newRowSelection = sortRowSelection([
            { rowNumber, registrationStatus },
            ...oldRowSelection.filter(
                (currentRowSelectionItem) => currentRowSelectionItem.rowNumber !== rowNumber,
            ),
        ]);

        return { ...state, data: { ...state.data, rowSelection: newRowSelection } };
    }),

    on(removeScannedCodeFromFlow, (state, { code }) => {
        const filteredScannedCodes = state.data.scannedCodes.filter(
            (existingCode) => existingCode.code !== code,
        );
        if (state.data.scannedCodes.length === filteredScannedCodes.length) return state;

        const removedCode = state.data.scannedCodes.find((existingCode) => existingCode.code === code);
        let rowSelection = state.data.rowSelection;
        if (removedCode?.codeType === 'location') {
            rowSelection = rowSelection.filter((r) => r.rowNumber !== removedCode.number);
        }

        return {
            ...state,
            data: {
                ...state.data,
                scannedCodes: filteredScannedCodes,
                rowSelection,
            },
        };
    }),
    on(userInputForScanFlowStarted, (state) => ({
        ...state,
        waitingForUserInput: true,
    })),
    on(userInputForScanFlowEnded, (state) => ({
        ...state,
        waitingForUserInput: false,
    })),
    on(scheduleScanFlowRegistration, (state, { scheduledRegistrationDate }) => ({
        ...state,
        scheduledRegistrationDate,
    })),
    on(
        cancelScheduledScanFlowRegistration,
        (state): ScanFlowState => ({
            ...state,
            scheduledRegistrationDate: undefined,
        }),
    ),
);

export const scanFlowFeature = createFeature({
    name: scanFlowFeatureKey,
    reducer,
});
